import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { get } from "lodash";
import { USER_INFO_KEY } from "../constants/common";
import { identify, reset } from "../services/analytics";
import { isImpersonatedToken } from "../helpers/impersonationHelper";

const parseUser = (rawUser) => {
  try {
    return JSON.parse(rawUser || "{}");
  } catch {
    return {};
  }
};

// Exported separately from resolveIdentity (which folds this into a plain
// `null`, indistinguishable from "no logged-in user") so the effect below
// can force reset() even on the very first render of an impersonated
// session - see the comment on that call for why that distinction matters.
export const isImpersonating = (rawUser) =>
  isImpersonatedToken(get(parseUser(rawUser), "token.accessToken"));

export const resolveIdentity = (userDetails, rawUser) => {
  const user = parseUser(rawUser);

  // An admin's own impersonated activity must never be attributed to the
  // customer they're viewing as, and it shouldn't be attributed to the
  // admin's own identity either - that would misattribute the customer's
  // usage instead. Treated the same as logged-out (null) so the effect
  // below calls reset() rather than identify() while impersonating.
  if (isImpersonatedToken(get(user, "token.accessToken"))) return null;

  const userInfo = get(user, "userInfo");
  const storeUserId = get(userDetails, [0, "UserId"]);
  if (!userInfo || !userInfo.userId || !storeUserId) return null;

  return {
    id: String(userInfo.userId),
    traits: {
      userType: user.userType || "",
      planName: get(userDetails, "0.PlanName"),
      planPeriod: get(userDetails, "0.period"),
    },
  };
};

export const identityKey = (identity) =>
  [
    identity.id,
    identity.traits.userType,
    identity.traits.planName,
    identity.traits.planPeriod,
  ].join("|");

// Never a real identityKey() value (those are always id/userType/plan
// pipe-joined), so it's safe to reuse lastKey.current as the "have we
// already reset for this impersonated session" marker below.
const IMPERSONATING_KEY = "__impersonating__";

const useAnalyticsIdentify = () => {
  const userDetails = useSelector((state) => state.userProfile.userDetails);
  const lastKey = useRef(null);

  useEffect(() => {
    try {
      const rawUser =
        typeof localStorage !== "undefined"
          ? localStorage.getItem(USER_INFO_KEY)
          : null;
      const identity = resolveIdentity(userDetails, rawUser);

      if (!identity) {
        // A page freshly loaded into an impersonated session never had a
        // prior identify() call to reset - lastKey.current is still null,
        // the same as a genuinely logged-out visitor - so without this
        // branch reset() never runs and whatever identity was already
        // persisted in this browser (typically the admin's own) keeps
        // attributing impersonated activity for the rest of the session.
        if (isImpersonating(rawUser)) {
          if (lastKey.current !== IMPERSONATING_KEY) {
            reset();
            lastKey.current = IMPERSONATING_KEY;
          }
          return;
        }

        if (lastKey.current) {
          reset();
          lastKey.current = null;
        }
        return;
      }

      const key = identityKey(identity);
      if (key === lastKey.current) return;

      identify(identity.id, identity.traits);
      lastKey.current = key;
    } catch {
      return;
    }
  }, [userDetails]);
};

export default useAnalyticsIdentify;
